import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import { meetingPointService, postService } from '../../core/services'
import * as constants from '../../static/constants'
import * as interfaces from '../../static/interfaces'
import injectGoogleMaps from '../../utils/injectGoogleMaps'
import { Button, InputText, InputTextArea, Toggle } from '../atoms'
import { Form } from '../molecules'

declare var window: interfaces.IWindow

const ADDRESS_FIELDS = {
  location: 'location',
  name: 'name',
  memo: 'memo',
}

interface IErrors {
  [key: string]: string | null
}

interface IProps {
  google: any
  post: interfaces.IPost
  meetingPoints: interfaces.IMeetingPoint[]
}

const meetingPointInitialValues = {
  id: null,
  location: '',
  name: '',
  memo: '',
  latitude: '',
  longitude: '',
}

const formatMeetingPoint = ({ id, location, name, memo, latitude, longitude }) => ({
  id,
  location,
  name,
  memo,
  latitude,
  longitude,
})

export const PostAddress: React.FC<IProps> = props => {
  const [editMeetingPoint, setEditMeetingPoint] = React.useState(null)
  const [meetingPoints, setMeetingPoints] = React.useState(props.meetingPoints)
  const [allowPickup, setAllowPickup] = React.useState(props.post.allow_pickup ?? true)
  const [map, setMap] = React.useState(null)
  const [marker, setMarker] = React.useState(null)
  const [coordinate, setCoordinate] = React.useState({
    latitude: null,
    longitude: null,
  })
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false)
  const [errors, setErrors] = React.useState<IErrors>({})

  React.useEffect(() => {
    const { latitude, longitude } = coordinate

    if (props.google) {
      createAutoComplete()
    }

    if (props.google && latitude && longitude) {
      const latLng = new props.google.maps.LatLng(latitude, longitude)

      if (map) {
        map.panTo(latLng)
        map.setZoom(15)
      } else {
        createMap(latLng)
      }

      if (marker) {
        marker.setPosition(latLng)
      }
    }
  }, [props.google, coordinate, editMeetingPoint])

  const createMap = async latLng => {
    const mapCanvas = document.getElementById('map')

    if (!mapCanvas) {
      return
    }

    const Options = {
      zoom: 15,
      center: latLng,
      mapTypeId: 'roadmap',
    }
    const initializedMap = new props.google.maps.Map(mapCanvas, Options)
    const defaultMarker = new props.google.maps.Marker({
      position: latLng,
      title: props.post.name,
      map: initializedMap,
    })

    setMarker(defaultMarker)
    setMap(initializedMap)
  }

  const createAutoComplete = () => {
    const input = document.getElementById('search')

    if (!input) {
      return
    }

    const autocomplete = new props.google.maps.places.Autocomplete(input)
    const onPlaceChanged = () => {
      const place = autocomplete.getPlace()

      setCoordinate({
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
      })
    }

    autocomplete.addListener('place_changed', onPlaceChanged)
  }

  const handleSubmit = async (initialValues, values) => {
    const params = {
      ...values,
      ...coordinate,
      post_id: props.post.id,
    }

    if (editMeetingPoint.id) {
      const { meetingPoint: updated, flush } = await meetingPointService.update(
        editMeetingPoint.id,
        params
      )
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      setMeetingPoints(current =>
        current.map(item => (item.id === updated.id ? formatMeetingPoint(updated) : item))
      )
      handleBackToIndex()
    } else {
      const { meetingPoint: created, flush } = await meetingPointService.create(params)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      setMeetingPoints(current => [...current, formatMeetingPoint(created)])
      handleBackToIndex()
    }
  }

  const handleDelete = async id => {
    const { flush } = await meetingPointService.delete(id)
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    setMeetingPoints(current => current.filter(item => item.id !== id))
  }

  const handleEdit = meetingPoint => {
    const { latitude, longitude } = meetingPoint
    setEditMeetingPoint(meetingPoint)
    setCoordinate({ latitude, longitude })
  }

  const handleCreate = meetingPoint => {
    setEditMeetingPoint(meetingPoint)
    setCoordinate({ latitude: null, longitude: null })
  }

  const handleBackToIndex = () => {
    setEditMeetingPoint(null)
    setMap(null)
    setMarker(null)
    setErrors({})
  }

  const handleUpdateForm = (updatedErrors, updatedIsSubmitEnabled) => {
    setErrors(updatedErrors)
    setIsSubmitEnabled(updatedIsSubmitEnabled)
  }

  const handleChangeAllowPickup = async (nextAllowPickup: boolean) => {
    const prevAllowPickup = allowPickup
    setAllowPickup(nextAllowPickup)
    try {
      const { flush } = await postService.updateAllowPickup(props.post.id, nextAllowPickup)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } catch (error) {
      setAllowPickup(prevAllowPickup)
      window.flashMessages.addMessage({
        text: 'Failed to update',
        type: 'error',
      })
    }
  }

  return (
    <Wrapper>
      <h2 className="Editor_Title">{I18n.t('post.input_meeting_point')}</h2>

      {editMeetingPoint && (
        <Form
          fields={ADDRESS_FIELDS}
          handleUpdateForm={handleUpdateForm}
          handleSubmit={handleSubmit}
        >
          <AutoComplete>
            <input
              id="search"
              type="text"
              name="location"
              defaultValue={editMeetingPoint?.location}
            />
          </AutoComplete>
          <div
            id="map"
            className="map"
            style={{ display: coordinate.latitude && coordinate.longitude ? 'block' : 'none' }}
          />
          <div className="FormItmes">
            <ul>
              <FormItem>
                <InputText
                  required={true}
                  name="name"
                  label="Name"
                  defaultValue={editMeetingPoint?.name}
                  error={errors.name}
                />
              </FormItem>
              <FormItem>
                <InputTextArea
                  name="memo"
                  label="Memo"
                  defaultValue={editMeetingPoint?.memo}
                  error={errors.memo}
                />
              </FormItem>
            </ul>
          </div>
          <div className="Editor_Footer">
            <Button handleClick={() => handleBackToIndex()}>
              {I18n.t('generic.back_to_index')}
            </Button>
            <Button primary={true}>
              {editMeetingPoint.id ? I18n.t('generic.update') : I18n.t('generic.create')}
            </Button>
          </div>
        </Form>
      )}

      {editMeetingPoint === null && (
        <MeetingPointsList>
          <ul>
            {meetingPoints.map(meetingPoint => (
              <li key={meetingPoint.id}>
                <div>
                  <div className="name">{meetingPoint.name}</div>
                  <div className="location">{meetingPoint.location}</div>
                </div>
                <div>
                  <Button primary={true} handleClick={() => handleEdit({ ...meetingPoint })}>
                    {I18n.t('generic.edit')}
                  </Button>
                  <Button handleClick={() => handleDelete(meetingPoint.id)}>
                    {I18n.t('generic.delete')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="buttons">
            <Button
              primary={true}
              handleClick={() => handleCreate({ ...meetingPointInitialValues })}
            >
              {I18n.t('generic.create_new')}
            </Button>
          </div>
          {props.post.post_type === 'private' && (
            <div className="pickupToggle">
              <Toggle
                name="allow_pickup"
                label={I18n.t('post.allow_pickup_input')}
                checked={allowPickup}
                onChangeHandler={e => handleChangeAllowPickup(e.target.checked)}
              />
            </div>
          )}
        </MeetingPointsList>
      )}
    </Wrapper>
  )
}

const MeetingPointsList = styled.ul`
  padding: 20px;

  li {
    padding: 8px 0;

    > div + div {
      margin-top: 10px;
    }
  }

  li + li {
    border-top: solid 1px ${constants.COLORS.divider};
  }

  .name {
    font-weight: bold;
  }

  .location {
    margin-top: 8px;
  }

  .buttons {
    margin-top: 24px;
  }

  .pickupToggle {
    margin-top: 24px;
    padding-top: 24px;
    border-top: solid 1px ${constants.COLORS.divider};
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
  }
`

const Wrapper = styled.div`
  .FormItmes {
    margin-top: 24px;
    padding-top: 24px;
    border-top: solid 1px ${constants.COLORS.divider};
  }

  .map {
    height: 320px;
    margin-top: 32px;
  }
`

const AutoComplete = styled.div`
  > input {
    display: block;
    width: 100%;
    padding: 12px;
    border: solid 1px ${constants.COLORS.divider};
    border-radius: 4px;
    font-size: 16px;
    transition: border 0.2s ease;
    outline: none;
    background: none;
  }
`
const FormItem = styled.li`
  & + & {
    margin-top: 16px;
  }

  .FormItem_Title {
    font-size: 15px;
    font-weight: bold;
  }
`

export default injectGoogleMaps(PostAddress)
